import type { CompAnalysisJob, CompAnalysisStageDetail, ListJobsResponse, CreateJobRequest } from './types'
import { API_CONFIG } from '@/shared/config'

const API_URL = API_CONFIG.baseUrl

async function getAuthHeaders(): Promise<HeadersInit> {
  const { getAuthToken } = await import('@trafiflow/shared-ui')
  const token = await getAuthToken()
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

export const competitorAnalysisService = {
  /**
   * Create a new competitor analysis job from a brand URL.
   */
  async createJob(request: CreateJobRequest): Promise<CompAnalysisJob> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/competitor-analysis/jobs`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to create job: ${response.status}`)
    }

    return response.json()
  },

  /**
   * List all competitor analysis jobs for the current tenant.
   */
  async listJobs(): Promise<ListJobsResponse> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/competitor-analysis/jobs`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to list jobs: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Get a single job with full stage status and (if complete) result.
   */
  async getJob(jobId: string): Promise<CompAnalysisJob> {
    const headers = await getAuthHeaders()

    const response = await fetch(`${API_URL}/competitor-analysis/jobs/${jobId}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to get job: ${response.status}`)
    }

    return response.json()
  },

  /**
   * Get detail for a specific stage including its artifact.
   */
  async getStageDetail(jobId: string, stageName: string): Promise<CompAnalysisStageDetail> {
    const headers = await getAuthHeaders()

    const response = await fetch(
      `${API_URL}/competitor-analysis/jobs/${jobId}/stages/${stageName}`,
      { method: 'GET', headers }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Failed to get stage: ${response.status}`)
    }

    return response.json()
  },
} as const
